import { CommonModule } from '@angular/common';
import { Component, Signal, computed, inject, signal, OnDestroy, WritableSignal } from '@angular/core';
import { ErmCapabilityApiClientService, ErmSdkModule, GetScaleAccountProgress, ScaleAccountProgressStatus, StageInfo } from 'erm-sdk';
import { EnvironmentUiService } from './services/environment-ui.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorNormalizerService } from './services/error-normalizer.service';
import { environment } from '../../../../environments/environment';

@Component({
  standalone: true,
  imports: [CommonModule, ErmSdkModule],
  selector: 'app-environment-services',
  template: `
@if (uiService.environment$()) {
  @if (isAwsStageProd === false) {
    <!-- Services panel -->
    <section class="tw-relative tw-flex tw-items-stretch tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white">
      <!-- Left colored rail -->
      <div class="tw-w-3 tw-rounded-l-xl {{ railColor$() }}"></div>

      <div class="tw-flex tw-items-center tw-justify-between tw-flex-1 tw-px-2 tw-py-2 tw-gap-2">

        <div class="tw-flex tw-flex-col tw-justify-center">
          <div class="tw-text-base tw-font-medium tw-text-slate-900">Services</div>
          <div class="{{ statusColor$() }} tw-text-sm tw-font-medium">{{ statusMessage$() }}</div>
        </div>

        <div class="tw-flex tw-flex-row tw-gap-2">

            @if (scalingStatus$() === progressStatus.IN_PROGRESS) {
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="tw-w-8 tw-h-8 tw-fill-orange-400 tw-animate-spin">
                <path d="M272 112C272 85.5 293.5 64 320 64C346.5 64 368 85.5 368 112C368 138.5 346.5 160 320 160C293.5 160 272 138.5 272 112zM272 528C272 501.5 293.5 480 320 480C346.5 480 368 501.5 368 528C368 554.5 346.5 576 320 576C293.5 576 272 554.5 272 528zM112 272C138.5 272 160 293.5 160 320C160 346.5 138.5 368 112 368C85.5 368 64 346.5 64 320C64 293.5 85.5 272 112 272zM480 320C480 293.5 501.5 272 528 272C554.5 272 576 293.5 576 320C576 346.5 554.5 368 528 368C501.5 368 480 346.5 480 320zM139 433.1C157.8 414.3 188.1 414.3 206.9 433.1C225.7 451.9 225.7 482.2 206.9 501C188.1 519.8 157.8 519.8 139 501C120.2 482.2 120.2 451.9 139 433.1zM139 139C157.8 120.2 188.1 120.2 206.9 139C225.7 157.8 225.7 188.1 206.9 206.9C188.1 225.7 157.8 225.7 139 206.9C120.2 188.1 120.2 157.8 139 139zM501 433.1C519.8 451.9 519.8 482.2 501 501C482.2 519.8 451.9 519.8 433.1 501C414.3 482.2 414.3 451.9 433.1 433.1C451.9 414.3 482.2 414.3 501 433.1z"/>
              </svg>
            }

            @if (isEnvironmentLoading$()) {
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="tw-w-8 tw-h-8 tw-fill-blue-400 tw-animate-spin">
                <path d="M272 112C272 85.5 293.5 64 320 64C346.5 64 368 85.5 368 112C368 138.5 346.5 160 320 160C293.5 160 272 138.5 272 112zM272 528C272 501.5 293.5 480 320 480C346.5 480 368 501.5 368 528C368 554.5 346.5 576 320 576C293.5 576 272 554.5 272 528zM112 272C138.5 272 160 293.5 160 320C160 346.5 138.5 368 112 368C85.5 368 64 346.5 64 320C64 293.5 85.5 272 112 272zM480 320C480 293.5 501.5 272 528 272C554.5 272 576 293.5 576 320C576 346.5 554.5 368 528 368C501.5 368 480 346.5 480 320zM139 433.1C157.8 414.3 188.1 414.3 206.9 433.1C225.7 451.9 225.7 482.2 206.9 501C188.1 519.8 157.8 519.8 139 501C120.2 482.2 120.2 451.9 139 433.1zM139 139C157.8 120.2 188.1 120.2 206.9 139C225.7 157.8 225.7 188.1 206.9 206.9C188.1 225.7 157.8 225.7 139 206.9C120.2 188.1 120.2 157.8 139 139zM501 433.1C519.8 451.9 519.8 482.2 501 501C482.2 519.8 451.9 519.8 433.1 501C414.3 482.2 414.3 451.9 433.1 433.1C451.9 414.3 482.2 414.3 501 433.1z"/>
              </svg>
            }

            @if (scalingErrorMessage$()) {
              <div class="tw-flex tw-items-center tw-gap-2 tw-text-rose-500">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="tw-w-4 tw-h-4 tw-fill-current">
                  <path d="M320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576zM320 384C302.3 384 288 398.3 288 416C288 433.7 302.3 448 320 448C337.7 448 352 433.7 352 416C352 398.3 337.7 384 320 384zM320 192C301.8 192 287.3 207.5 288.6 225.7L296 329.7C296.9 342.3 307.4 352 319.9 352C332.5 352 342.9 42.3 343.8 29.7L351.2 225.7C352.5 207.5 338.1 192 319.8 192z"/>
                </svg>
                <div class="tw-text-xs tw-font-medium">{{ scalingErrorMessage$() }}</div>
              </div>
            }
        </div>

        <button
          type="button"
          [disabled]="!servicesButtonEnabled$()"
          [attr.aria-disabled]="!servicesButtonEnabled$()"
          (click)="servicesButtonClick()"
          class="tw-h-10 tw-px-4 tw-rounded-md tw-font-semibold tw-shadow-sm tw-transition"
          [ngClass]="{
            'tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400': !servicesButtonEnabled$(),
            'tw-bg-indigo-600 hover:tw-bg-indigo-700 tw-text-white': servicesButtonEnabled$()}"
        >
          {{ servicesButtonLabel$() }}
        </button>
      </div>
    </section>
  }
}
@else {
  <div class="tw-flex tw-items-center tw-justify-center tw-h-full">
    <p>No environment provided</p>
  </div>
}
`,
})
export class EnvironmentServicesComponent implements OnDestroy {
  private readonly errorNormalizer = inject(ErrorNormalizerService);
  public readonly uiService = inject(EnvironmentUiService);
  private readonly ermCapabilityApiClient = inject(ErmCapabilityApiClientService);
  public readonly isAwsStageProd = environment.stage === StageInfo.PROD;
  private readonly servicesHealthy$: Signal<boolean> = computed(() => this.uiService.environment$()?.scalableServicesHealthy === true);
  public progressStatus = ScaleAccountProgressStatus;
  public readonly scalingStatus$ = signal<ScaleAccountProgressStatus>(ScaleAccountProgressStatus.NOT_STARTED);
  public readonly scalingErrorMessage$ = signal<string | null>(null);
  private scalingTimer: number | null = null;
  private readonly scalingTimerDelay = 10000;
  public readonly isEnvironmentLoading$: WritableSignal<boolean> = signal(false);
  public readonly servicesButtonEnabled$: Signal<boolean> = computed(() => {
    const isFunctional = this.uiService.isFunctional$();
    const servicesHealthy = this.servicesHealthy$();
    const scalingStatus = this.scalingStatus$();

    if (scalingStatus === ScaleAccountProgressStatus.IN_PROGRESS) {
      return false;
    }

    return isFunctional && !servicesHealthy;
  });

  public readonly servicesButtonLabel$: Signal<string> = computed(() => {
    const isFunctional = this.uiService.isFunctional$();
    const servicesHealthy = this.servicesHealthy$();
    const environment = this.uiService.environment$();

    if (isFunctional && environment?.inProgressScalingExecutionArn) {
      return 'Check Progress';
    }

    return isFunctional && servicesHealthy ? 'Ready' : isFunctional ? 'Start' : 'Unavailable';
  });

  public readonly railColor$: Signal<string> = computed(() => {
    const isFunctional = this.uiService.isFunctional$();
    const servicesHealthy = this.servicesHealthy$();
    const scalingStatus = this.scalingStatus$();

    if (scalingStatus === ScaleAccountProgressStatus.IN_PROGRESS) {
      return 'tw-bg-orange-400';
    }

    return !isFunctional ? 'tw-bg-gray-400' : servicesHealthy ? 'tw-bg-green-500' : 'tw-bg-rose-500';
  });

  public readonly statusColor$: Signal<string> = computed(() => {
    const isFunctional = this.uiService.isFunctional$();
    const servicesHealthy = this.servicesHealthy$();
    const scalingStatus = this.scalingStatus$();
    const environment = this.uiService.environment$();

    if (scalingStatus === ScaleAccountProgressStatus.IN_PROGRESS) {
      return 'tw-text-orange-400';
    }

    if (isFunctional && environment?.inProgressScalingExecutionArn) {
      return 'tw-text-orange-400';
    }

    return !isFunctional ? 'tw-text-gray-400' : servicesHealthy ? 'tw-text-green-500' : 'tw-text-rose-500';
  });

  public readonly statusMessage$: Signal<string> = computed(() => {
    const isFunctional = this.uiService.isFunctional$();
    const servicesHealthy = this.servicesHealthy$();
    const scalingStatus = this.scalingStatus$();
    const environment = this.uiService.environment$();

    if (scalingStatus === ScaleAccountProgressStatus.IN_PROGRESS) {
      return 'Scaling';
    }

    if (isFunctional && environment?.inProgressScalingExecutionArn) {
      return 'Scaling';
    }

    if (this.isEnvironmentLoading$()) {
      return 'Refreshing';
    }

    return !isFunctional ? 'Unavailable' : servicesHealthy ? 'Started' : 'Stopped';
  });

  constructor() { 
    this.uiService.setSharedLoading(this.isEnvironmentLoading$);
  }

  public async servicesButtonClick(): Promise<void> {

    const environment = this.uiService.environment$();
    const isFunctional = this.uiService.isFunctional$();

    if (isFunctional && environment?.inProgressScalingExecutionArn) {
      this.scalingStatus$.set(ScaleAccountProgressStatus.IN_PROGRESS);
      this.scalingErrorMessage$.set(null);
      await this.getScalingProgress();
    }
    else {
      this.scalingErrorMessage$.set(null);

      if (!this.uiService.environment$()?.scalableServicesHealthy) {
        await this.scaleEnvironmentServicesUp();
      }
      else {
        this.scalingStatus$.set(ScaleAccountProgressStatus.FAILED);
      }
    }
  }

  public async scaleEnvironmentServicesUp(): Promise<void> {
    try {
      this.scalingStatus$.set(ScaleAccountProgressStatus.IN_PROGRESS);
      this.scalingErrorMessage$.set(null);
      this.uiService.scaleProgress$.set(null);

      const response = await this.ermCapabilityApiClient.startScalingEnvironmentServices(this.uiService.environment$()?.environmentId);

      if (response.ok) {
        const environment = this.uiService.environment$();
        environment.inProgressScalingExecutionArn = response.body?.stepFunctionExecutionArn;
        this.uiService.setEnvironment(environment);
        this.scalingErrorMessage$.set(null);
        await this.getScalingProgress();
      }
      else {
        const message = 'Error during start of AppStream environment services.';
        this.scalingErrorMessage$.set(message);
        this.scalingStatus$.set(ScaleAccountProgressStatus.FAILED);
        throw Error(message);
      }

    }
    catch (error: HttpErrorResponse | Error | unknown) {
      const norm = this.errorNormalizer.extract(error);
      this.scalingErrorMessage$.set(norm.message);
      this.scalingStatus$.set(ScaleAccountProgressStatus.FAILED);
      throw error;
    }
    finally {

    }
  }

  public async getScalingProgress(): Promise<void> {
    try {
      const environment = this.uiService.environment$();
      if (!environment?.inProgressScalingExecutionArn) {
        console.log('No inProgressScalingExecutionArn found');
        return;
      }

      const response = await this.ermCapabilityApiClient.getScalingProgress(environment.inProgressScalingExecutionArn);

      if (response.ok) {
        const progress: GetScaleAccountProgress = response.body;

        this.scalingStatus$.set(progress.status);
        this.uiService.scaleProgress$.set(progress);
        
        // Only continue polling if scaling is still in progress
        if (progress.status === ScaleAccountProgressStatus.IN_PROGRESS) {

          // Clear any existing timer before setting a new one
          if (this.scalingTimer) {
            clearTimeout(this.scalingTimer);
          }
          // Delay to get the progress status from the backend
          this.scalingTimer = window.setTimeout(() => {
            this.getScalingProgress();
          }, this.scalingTimerDelay);
        }
        else if (progress.status === ScaleAccountProgressStatus.FAILED) {
          this.scalingErrorMessage$.set('Scaling failed.');
        }
        else if (progress.status === ScaleAccountProgressStatus.SUCCEEDED) {
          this.uiService.reloadEnvironment();
        }
      }
      else {
        throw response;
      }
    } catch (error) {
      const norm = this.errorNormalizer.extract(error);
      this.scalingErrorMessage$.set(norm.message);
      throw error;
    }
  }

  public ngOnDestroy(): void {
    // Clear the scaling timer when the component is destroyed
    if (this.scalingTimer) {
      clearTimeout(this.scalingTimer);
      this.scalingTimer = null;
    }
  }
}
